/* eslint-disable no-useless-concat */
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { ORDER_TYPES, ORDER_TYPE_NAMES } from '@/constants/orderTypes'
import { TIME_TYPES } from '@/constants/timeTypes'
import useFormat from '@/views/utils'

dayjs.extend(customParseFormat)

const { formatDate } = useFormat()

const getCloseReason = e => {
    return !e.close_reason
        ? ''
        : e.close_reason.name === 'Другое'
        ? e.close_comment
        : e.close_reason.name
}

const getOperator = operator => {
    return `${operator?.last_name || ''} ${operator?.first_name || ''}`
}

const getFormatDiscount = (price, finalPrice) => {
    const f = parseFloat(Math.round((price - finalPrice) * 100) / 100)
    if (!f) return 0
    return f.toFixed(0)
}

const rawAddressWithoutCountry = address => {
    return address?.split(',')?.slice(1)?.join(',') ?? '-'
}

const buildGroup4 = e => {
    const nameType = e.order_type?.id || ''
    const result = {
        sector: e.order_type?.name || '-',
        address: '',
        street: e.address?.street || '',
        house: `д: ${e.address?.house || '-'}`,
        entrance: `подъезд: ${e.address?.entrance || '-'}`,
        floor: `этаж: ${e.address?.floor || '-'}`,
        intercom: `домофон: ${e.address?.intercom || '-'}`,
        flat: `кв: ${e.address?.flat || '-'}`,
    }

    if (e.address && e.address.raw_address) {
        result.address = rawAddressWithoutCountry(e.address.raw_address)
    }

    switch (nameType) {
        case ORDER_TYPES.SELF:
            return {
                sector: '',
                address: '',
                isSelfPickup: true,
            }
        case ORDER_TYPES.PREORDER:
            return {
                sector: '',
                address: '',
                isPreorder: true,
            }
        case ORDER_TYPES.STAFF_OWN:
        case ORDER_TYPES.STAFF_OTHER:
            return {
                sector: '',
                address: '',
                isStaffOrder: true,
            }
        case ORDER_TYPES.SUBDELIVERY:
            // Для 'Довоз' базовая структура подходит
            return result
        case ORDER_TYPES.DELIVERY:
            // Для 'Доставка' подходит базовая структура
            return {
                ...result,
                sector: '',
            }
        default:
            return {}
    }
}

const getOrderRow = e => {
    return {
        groupMinimal1: {
            department: e.division?.name || '',
        },
        groupMinimal2: {
            num: e.num,
            editable: e.editable,
            lock_by_user:
                e.editable && !e.lock_by_user
                    ? ''
                    : `${e.lock_by_user.first_name} ${e.lock_by_user.last_name}`,
        },
        groupMinimal3: {
            nameCustomer: e.guest?.full_name || '',
        },
        groupMinimal4: {
            phone: e.phone || '',
        },
        groupMinimal5: buildGroup4(e),
        groupMinimal6: { createdDate: e.created_date },
        groupMinimal7: {
            deliveredDate: e.delivered_date || '',
        },
        groupMinimal8: {
            price: e.payments?.reduce((acc, elem) => acc + elem.price, 0) || 0,
        },
        groupMinimal9: {
            discount: getFormatDiscount(
                e.payments?.reduce((acc, elem) => acc + elem.price, 0) || 0,
                e.payments?.reduce((acc, elem) => acc + elem.final_price, 0) ||
                    0
            ),
        },
        groupMinimal10: {
            final_price:
                e.payments?.reduce((acc, elem) => acc + elem.final_price, 0) ||
                0,
        },
        groupMinimal11: {
            status: e.status?.name || '',
        },
        groupMinimal12: `${e.operator?.last_name || ''} ${
            e.operator?.first_name?.slice(0, 1) || ''
        }`,
        groupMinimal13: e.courier?.name || '',
        groupMinimal14: {
            status_on_the_way_date: e.order_date?.status_on_the_way_date || '',
        },
        groupMinimal15: {
            status_done_date: e.order_date?.status_done_date || '',
        },

        group1: {
            status: e.status?.name || '',
            color: e.status?.color || '#333E3F',
            department: e.division?.name || '',
            editable: e.editable,
            lock_by_user:
                e.editable && !e.lock_by_user
                    ? ''
                    : `${e.lock_by_user.first_name} ${e.lock_by_user.last_name}`,
            closeReason: getCloseReason(e),
        },
        group2: {
            createdDate: e.created_date,
            num: e.num,
        },
        group3: {
            phone: e.phone || '',
            nameCustomer: e.guest?.full_name || '',
            isBlacklisted: e.guest?.is_blacklisted || false,
            blacklistedReason: e.guest?.blacklist_reason || '',
            isVip: e.guest?.is_vip || false,
        },
        group4: buildGroup4(e),
        group5: {
            extendedNumber: e.ext_id || '',
            operator: getOperator(e.operator),
        },
        group6: {
            deliveredDate: e.delivered_date || '',
            payments: e.payments || [],
        },
        group7: {
            vip: e.guest?.is_vip || false,
        },
        id: e.id,
        editable: e.editable,
        errors: !!e.errors,
        lock_by_user:
            e.editable && !e.lock_by_user
                ? ''
                : `${e.lock_by_user.first_name} ${e.lock_by_user.last_name}`,

        color: e.status?.color || '#333E3F',
    }
}

// convert nanosecionds HH:MM:SS with 0 if less than 10
const nsToMinutes = ns => {
    const seconds = Math.floor(ns / 1000000000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    return `${hours < 10 ? '0' : ''}${hours}:${minutes % 60 < 10 ? '0' : ''}${
        minutes % 60
    }:${seconds % 60 < 10 ? '0' : ''}${seconds % 60}`
}
const getForemanOrderRow = e => ({
    group1: {
        num: e.num,
        department: e.division?.name || '',
    },
    group2: {
        status: e.status?.name || '',
        color: e.status?.color || '#333E3F',
        closeReason: getCloseReason(e),
    },
    group3: {
        phone: e.phone || '',
        nameCustomer: e.guest?.full_name || '',
    },
    group3b: {
        gastronomy: e.time_type === TIME_TYPES.interval,
        box: e.labels?.length && e.labels[0] === 'Боксы',
    },
    group4: {
        createdDate: e.created_date,
    },
    group5: {
        deliveredDate: e.delivered_date || '',
        gastronomy: e.time_type === TIME_TYPES.interval,
    },
    group6: buildGroup4(e),
    group7: {
        price: e.payments?.reduce((acc, elem) => acc + elem.price, 0) || 0,
        final_price:
            e.payments?.reduce((acc, elem) => acc + elem.final_price, 0) || 0,
    },
    group8: {
        courier: e.courier?.name || '',
        status: e.courier?.status || '',
    },
    group9: {
        transferred_to_delivery: e.order_date.status_on_the_way_date || '',
    },
    group10: {
        done_date: e.order_date.status_done_date || '',
    },
    id: e.id,
    editable: e.editable,
    lock_by_user:
        e.editable && !e.lock_by_user
            ? ''
            : `${e.lock_by_user.first_name} ${e.lock_by_user.last_name}`,
})

const getSession = e => ({
    userName: e.Name,
    channel: e.Channel,
    status: e.Active ? e.NaumenState : 'away',
    site: e.Site,
    calling: e.Call,
    breaks: e.BreaksDuration ? nsToMinutes(e.BreaksDuration) : null,
    active: e.Active,
    lastOrderDestributionTime: formatDate(e.LastOrderDestributionTime),
})

function newAbortSignal(timeout) {
    const abortController = new AbortController()
    setTimeout(() => abortController.abort(), timeout || 0)

    return abortController.signal
}

const getFormattedShortAddress = address => {
    return address.street
        ? `${address.street}, ${address.house.replace(':', '.')}`
        : null
}

const getFullAddressForCompactView = address => {
    if (address.isSelfPickup) {
        return ORDER_TYPE_NAMES[ORDER_TYPES.SELF]
    }
    if (address.isPreorder) {
        return ORDER_TYPE_NAMES[ORDER_TYPES.PREORDER]
    }

    return address?.address
        ? `${address.address} ${address.entrance} ${address.flat} ${address.floor} ${address.intercom}`
        : `${address.street} ${address.house} ${address.flat} ${address.entrance} ${address.floor} ${address.intercom}`
}

const getShortAddressForCompactView = address => {
    if (address.isSelfPickup) {
        return ORDER_TYPE_NAMES[ORDER_TYPES.SELF]
    }
    if (address.isPreorder) {
        return ORDER_TYPE_NAMES[ORDER_TYPES.PREORDER]
    }

    return address?.address && !address.street
        ? address.address
        : getFormattedShortAddress(address)
}

const DESCTOP_DEVICE_TYPES = ['Windows', 'Mac', 'Linux']

const getDeviceType = () => {
    const { userAgent } = navigator

    if (/android/i.test(userAgent)) {
        return 'Android'
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return 'iOS'
    }
    if (/Windows Phone/i.test(userAgent)) {
        return 'Windows Phone'
    }
    if (/Macintosh/i.test(userAgent)) {
        return 'Mac'
    }
    if (/Windows/i.test(userAgent)) {
        return 'Windows'
    }
    if (/Linux/i.test(userAgent)) {
        return 'Linux'
    }

    return 'Unknown'
}

const getIsDeviceMobile = () => {
    const deviceType = getDeviceType()
    return !DESCTOP_DEVICE_TYPES.includes(deviceType)
}

export {
    getOrderRow,
    getCloseReason,
    getOperator,
    getSession,
    getForemanOrderRow,
    buildGroup4,
    newAbortSignal,
    getFullAddressForCompactView,
    getShortAddressForCompactView,
    getIsDeviceMobile,
    rawAddressWithoutCountry,
}
